/**
* For fetching usage data (daily, monthly, yearly)
 * used across both Dashboard and Customer modules.
 *
 * Shared TanStack Query hooks for fetching usage data (daily, monthly, yearly)
 * used by both Dashboard and Customer modules.
 * 
 * - If an "id" is passed  fetches usage data for that specific customer.
 * - If "id" is omitted  fetches aggregated "Total Power Usage" data across all customers (Dashboard/Home screen).
 *
 * Refer to the `queries` folders inside the Shared module for further details.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { useCustomerService } from '../services/customers.service';

import type {
  Customer,
  CustomerDetail,
  CreateCustomer,
  GetCustomersParams,
  GetCustomersResponse,
  CustomerStats,
  GetCustomersBillingParams,
  UploadPOAPayload,
} from '../models/customer.model';
import { useToast } from '@/shared/hooks/useToast';
import { useTranslation } from 'react-i18next';
import { BackendErrorResponse } from '@/shared';
import { TelemetryApiResponse } from '../models';

export const CUSTOMER_QUERY_KEYS = {
  all: ['customers'] as const,
  lists: () => [...CUSTOMER_QUERY_KEYS.all, 'list'] as const,
  list: (filters?: GetCustomersParams) =>
    [...CUSTOMER_QUERY_KEYS.lists(), filters] as const,
  details: () => [...CUSTOMER_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...CUSTOMER_QUERY_KEYS.details(), id] as const,
  stats: () => [...CUSTOMER_QUERY_KEYS.all, 'stats'] as const,
  billingHistorylists: () =>
    [...CUSTOMER_QUERY_KEYS.all, 'customerbilling'] as const,
  billingFilterlist: (filters: GetCustomersBillingParams) =>
    [...CUSTOMER_QUERY_KEYS.billingHistorylists(), filters] as const,
};

export function useCustomerMutations() {
  const service = useCustomerService();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { t } = useTranslation('customers');

  const invalidateLists = () =>
    queryClient.invalidateQueries({ queryKey: CUSTOMER_QUERY_KEYS.lists() });

  const notification = (
    message: string,
    type: 'info' | 'success' | 'error' | 'warning' | 'message'
  ) => {
    notify(message, { position: 'bottom-right', duration: 2000 }, type);
  };

  const extractErrorMessage = (error: unknown): string => {
    const be = error as BackendErrorResponse | undefined;
    const data = be?.response?.data as unknown;
    // prefer nested inner error when available
    if (data && typeof data === 'object') {
      const d = data as Record<string, unknown>;
      const nested = d.data as Record<string, unknown> | undefined;
      if (nested && typeof nested.error === 'string') return nested.error;
      if (typeof d.error === 'string') return d.error;
      if (typeof d.message === 'string') return d.message;
      if (typeof d.error_description === 'string') return d.error_description;
    }
    return be?.message || String(error);
  };

  return {
    createCustomer: useMutation({
      mutationFn: (customerData: CreateCustomer) =>
        service.createCustomer(customerData),
      onSuccess: (created: CustomerDetail | undefined) => {
        notification(
          t('customerActionNotificationSuccess', {
            action: t('creation'),
          }),
          'success'
        );
        invalidateLists();

        // If server returned an id for the created customer, invalidate its detail cache
        const createdId = created?.id;
        if (createdId) {
          queryClient.invalidateQueries({
            queryKey: CUSTOMER_QUERY_KEYS.detail(createdId),
            exact: true,
          });
        }
      },
      onError: (error: unknown) => {
        notification(extractErrorMessage(error), 'error');
      },
    }),

    updateCustomer: useMutation<
      CustomerDetail,
      unknown,
      { id: string; data: Partial<CustomerDetail | Customer> }
    >({
      mutationFn: vars => {
        if (!vars?.id) throw new Error('updateCustomer called without id');
        return service.updateCustomer(vars.id, vars.data);
      },
      onSuccess: (updated: CustomerDetail | undefined, vars) => {
        // Prefer id from mutation variables, fallback to server-returned id
        const targetId = vars?.id ?? updated?.id;
        void queryClient.invalidateQueries({
          queryKey: CUSTOMER_QUERY_KEYS.detail(targetId),
          exact: true,
        });

        notification(
          t('customerActionNotificationSuccess', {
            action: t('updateCustomer') ?? 'updated',
          }),
          'success'
        );
        invalidateLists();
      },
      onError: (error: unknown) => {
        notification(extractErrorMessage(error), 'error');
      },
    }),

    cancelSubscription: useMutation({
      mutationFn: (id: string) => service.cancelSubscription(id),
      onSuccess: (_, id) => {
        queryClient.removeQueries({
          queryKey: CUSTOMER_QUERY_KEYS.detail(id),
        });
        notification(
          t('customerActionNotificationSuccess', {
            action: 'subscription cancelled',
          }),
          'success'
        );
        invalidateLists();
      },
      onError: (error: BackendErrorResponse) => {
        notification(error.response?.data?.message ?? error.message, 'error');
      },
    }),

    giftEnergy: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: { kWh: number } }) =>
        service.giftEnergy(id, payload),
      onSuccess: (_, variables) => {
        queryClient.removeQueries({
          queryKey: CUSTOMER_QUERY_KEYS.detail(variables.id),
        });

        // Revalidate billing history api on success
        queryClient.invalidateQueries({
          queryKey: CUSTOMER_QUERY_KEYS.billingHistorylists(),
        });

        notification(
          t('customerActionNotificationSuccess', {
            action: 'gift sent',
          }),
          'success'
        );
        invalidateLists();
      },
      onError: (error: BackendErrorResponse) => {
        notification(error.response?.data?.message ?? error.message, 'error');
      },
    }),

    uploadPOA: useMutation({
      mutationFn: (payload: UploadPOAPayload) => service.uploadPOA(payload),

      onSuccess: () => {
        // Revalidate customer detail API on success
        queryClient.invalidateQueries({
          queryKey: CUSTOMER_QUERY_KEYS.details(),
        });

        notification(t('powerAttorneySuccess'), 'success');

      },

      onError: (error: BackendErrorResponse) => {
        notification(error.response?.data?.message ?? error.message, 'error');
      },
    }),

    useListCustomersQuery: (filters?: GetCustomersParams, options?: { enabled?: boolean }) => {
      return useQuery<GetCustomersResponse>({
        queryKey: CUSTOMER_QUERY_KEYS.list(filters),
        queryFn: () => service.getCustomers(filters || {}),
        staleTime: 5 * 60 * 1000,
        ...options
      });
    },
    

    useFetchCustomerBilling: (filters: GetCustomersBillingParams) => {
      return useQuery({
        queryKey: CUSTOMER_QUERY_KEYS.billingFilterlist(filters),
        queryFn: () => service.getcustomerBillng(filters),
        staleTime: 5 * 60 * 1000,
      });
    },

    useNotificationCustomersQuery: (filters?: GetCustomersParams) => {
      return useQuery<GetCustomersResponse>({
        queryKey: CUSTOMER_QUERY_KEYS.list(filters),
        queryFn: () => service.getCustomersLists(filters || {}),
        staleTime: 5 * 60 * 1000,
      });
    },

    useCustomerStatsQuery: () => {
      return useQuery<CustomerStats>({
        queryKey: CUSTOMER_QUERY_KEYS.stats(),
        queryFn: () => service.getCustomerStats(),
        staleTime: 5 * 60 * 1000,
      });
    },

    useGetCustomerByIdQuery: (id: string) => {
      return useQuery<CustomerDetail>({
        queryKey: CUSTOMER_QUERY_KEYS.detail(id),
        queryFn: () => service.getCustomerById(id),
        enabled: !!id,
        staleTime: 5 * 60 * 1000,
        placeholderData: keepPreviousData,
      });
    },
    
    useGetCustomerTelemetryQuery: (
      id: string | undefined,
      params: Record<string, unknown> | undefined
    ) => {
      return useQuery<TelemetryApiResponse>({
        queryKey: ['customers', 'telemetry', id, params],
        queryFn: () => {
          if (!id) throw new Error('Missing id for telemetry query');
          return service.getCustomerTelemetry(id, params || {});
        },
        enabled: !!id,
        staleTime: 60 * 1000,
      });
    },
  };
}
